import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Autocomplete, Box, Button, Stack, Switch, TextField, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { sendCustomNotificationForAllUsers, sendCustomMessageForUsers } from "../../Requests/NotificationService.ts";
import { getUsers } from "../../Requests/UserService.ts";
import { MemberModel } from "../../Models/Member.ts";
import HtmlEditor from "../HtmlEditorView/HtmlEditor.jsx";


export default function NotificationConfigurationView() {
  const [title, setTitle] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const [buttonLabel, setButtonLabel] = useState<string>("")
  const [buttonLink, setButtonLink] = useState<string>("")
  const [sendToAllMembers, setSendToAllMembers] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [members, setMembers] = useState<MemberModel[]>()
  const [selectedMembers, setSelectedMembers] = useState<MemberModel[]>([])

  const getPlainTextFromHtml = useCallback((html: string): string => {
    if (!html) {
      return ""
    }
    return html
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  }, [])

  const handleSendNotification = async (): Promise<void> => {
    const payload = {
      title: title.trim(),
      message: message.trim(),
      buttonLabel: buttonLabel.trim(),
      buttonLink: buttonLink.trim()
    }

    try {
      setIsSubmitting(true)

      if (sendToAllMembers) {
        await sendCustomNotificationForAllUsers(payload)
      } else {
        const userIds = selectedMembers
          .map((member) => member.objectId)
          .filter((id): id is string => id !== undefined)

        await sendCustomMessageForUsers(payload, userIds)
      }

      setTitle("")
      setMessage("")
      setButtonLabel("")
      setButtonLink("")
      setSelectedMembers([])
      alert("Done")
    } catch (error: any) {
      // Показываем админу понятное сообщение об ошибке
      const message = error?.message || "Something went wrong while sending the notification."
      alert(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const fetchData = useCallback(async () => {
    const users = await getUsers()
    setMembers(users)
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const isCtaSectionValid = useMemo(() => {
    const hasButtonLabel = buttonLabel.trim().length > 0
    const hasButtonLink = buttonLink.trim().length > 0

    return (hasButtonLabel && hasButtonLink) || (!hasButtonLabel && !hasButtonLink)
  }, [buttonLabel, buttonLink])

  const isSubmitDisabled = useMemo(() => {
    const hasRecipients = sendToAllMembers || selectedMembers.length > 0
    const hasTitle = title.trim().length > 0
    const plainMessage = getPlainTextFromHtml(message)
    const hasMessage = plainMessage.length > 0

    return !hasRecipients || !hasTitle || !hasMessage || !isCtaSectionValid || isSubmitting
  }, [getPlainTextFromHtml, isCtaSectionValid, isSubmitting, message, selectedMembers, sendToAllMembers, title])

  const recipientsPlaceholder = sendToAllMembers ? "All members will receive this message" : "Select member(s)"
  const hasPartialCta = (buttonLabel.trim().length > 0) !== (buttonLink.trim().length > 0)

  const fieldLabelStyles = {
    color: "#646464",
    fontSize: 14,
    lineHeight: "20px"
  }

  const baseFieldStyles = {
    "& .MuiOutlinedInput-root": {
      alignItems: "flex-start",
      borderRadius: "8px",
      backgroundColor: "#FCFCFC",
      "& fieldset": {
        borderColor: "#D9D9D9"
      },
      "&:hover fieldset": {
        borderColor: "#C4C4C4"
      },
      "&.Mui-focused fieldset": {
        borderColor: "#32355A"
      }
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(100, 100, 100, 0.72)",
      opacity: 1
    }
  }

  const sectionTitleStyles = {
    color: "#202020",
    fontSize: 18,
    fontWeight: 500,
    lineHeight: "28px"
  }

  return (
    <Box
      sx={{
        px: 3,
        py: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <Stack spacing={1}>
        <Typography sx={fieldLabelStyles}>Recipient(s)</Typography>
        <Autocomplete
          multiple
          disabled={sendToAllMembers}
          fullWidth
          popupIcon={<ExpandMoreRoundedIcon />}
          options={members ?? []}
          value={selectedMembers}
          isOptionEqualToValue={(option, value) => option.objectId === value.objectId}
          getOptionLabel={(option) => `${option.name ?? ""} ${option.surname ?? ""}`.trim()}
          filterSelectedOptions
          onChange={(_, value) => setSelectedMembers(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder={recipientsPlaceholder}
              helperText={!sendToAllMembers && selectedMembers.length === 0 ? "Select at least one member" : " "}
              sx={baseFieldStyles}
            />
          )}
          sx={{
            ...baseFieldStyles,
            width: "100%",
            "& .MuiAutocomplete-inputRoot": {
              minHeight: 48,
              padding: "4px 12px"
            }
          }}
        />
      </Stack>

      <Stack spacing={2}>
        <Typography sx={fieldLabelStyles}>Send to all members</Typography>
        <Switch
          checked={sendToAllMembers}
          onChange={(event) => setSendToAllMembers(event.target.checked)}
          sx={{
            width: 48,
            height: 24,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: "2px",
              "&.Mui-checked": {
                transform: "translateX(24px)",
                color: "#FFFFFF",
                "& + .MuiSwitch-track": {
                  backgroundColor: "#32355A",
                  opacity: 1
                }
              }
            },
            "& .MuiSwitch-thumb": {
              width: 20,
              height: 20,
              boxShadow: "none"
            },
            "& .MuiSwitch-track": {
              borderRadius: "999px",
              backgroundColor: "#D9D9D9",
              opacity: 1
            }
          }}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography sx={fieldLabelStyles}>Title</Typography>
        <TextField
          fullWidth
          value={title}
          onChange={(event) => setTitle(event.target.value.slice(0, 50))}
          placeholder="Add title (max 50 characters)"
          helperText={`${title.length}/50`}
          sx={baseFieldStyles}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography sx={fieldLabelStyles}>Message</Typography>
        <Box sx={{ width: "100%" }}>
          <HtmlEditor
            initialValue={message}
            onChange={setMessage}
            minHeight={317}
            placeholder={"Add message"}
          />
        </Box>
      </Stack>

      <Stack spacing={2}>
        <Typography sx={sectionTitleStyles}>Call to action</Typography>

        <Stack spacing={1}>
          <Typography sx={fieldLabelStyles}>Button label</Typography>
          <TextField
            fullWidth
            value={buttonLabel}
            onChange={(event) => setButtonLabel(event.target.value)}
            placeholder="Add button label"
            sx={baseFieldStyles}
          />
        </Stack>

        <Stack spacing={1}>
          <Typography sx={fieldLabelStyles}>Button link</Typography>
          <TextField
            fullWidth
            value={buttonLink}
            onChange={(event) => setButtonLink(event.target.value)}
            placeholder="Add link"
            sx={baseFieldStyles}
            error={hasPartialCta}
            helperText={hasPartialCta ? "Fill both CTA fields or leave both empty" : " "}
          />
        </Stack>
      </Stack>

      <Box
        display="flex"
        justifyContent="right"
      >
        <Button
          disabled={isSubmitDisabled}
          variant="contained"
          size="large"
          onClick={() => handleSendNotification()}
          sx={{
            minWidth: 143,
            height: 40,
            px: 2,
            borderRadius: "999px",
            backgroundColor: "#32355A",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "#2B2D4D"
            }
          }}
        >
          {isSubmitting ? "Sending..." : "Send message"}
        </Button>
      </Box>
    </Box>
  )
}
